<!--
  Component Library Manager - Manage Component Types

  FOR: pricing-portal-laravel/resources/js/components/sites/documentation-tabs/sax/ComponentLibraryManager.vue

  CRUD operations for SAX component types
  Import/export component library
-->

<template>
  <q-dialog
    :model-value="modelValue"
    maximized
    transition-show="slide-up"
    transition-hide="slide-down"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <q-card>
      <q-toolbar class="bg-primary text-white">
        <q-toolbar-title>
          <q-icon name="library_books" class="q-mr-sm" />
          Component Library Manager
        </q-toolbar-title>

        <q-btn flat round dense icon="close" @click="closeDialog" />
      </q-toolbar>

      <q-card-section class="q-pa-none">
        <q-splitter v-model="splitterModel" :limits="[40, 70]">
          <!-- Left Panel: Component List -->
          <template #before>
            <div class="q-pa-md">
              <!-- Toolbar -->
              <div class="row q-gutter-sm q-mb-md">
                <q-input
                  v-model="searchQuery"
                  dense
                  outlined
                  placeholder="Search components..."
                  class="col"
                  clearable
                >
                  <template #prepend>
                    <q-icon name="search" />
                  </template>
                </q-input>

                <q-btn
                  icon="add"
                  color="primary"
                  label="New"
                  @click="createNewComponent"
                />

                <q-btn
                  icon="upload"
                  color="secondary"
                  outline
                  label="Import"
                  @click="importLibrary"
                />

                <q-btn
                  icon="download"
                  color="secondary"
                  outline
                  label="Export"
                  @click="exportLibrary"
                />
              </div>

              <!-- Component Table -->
              <q-table
                :rows="filteredComponents"
                :columns="columns"
                row-key="id"
                :loading="isLoading"
                :pagination="pagination"
                dense
                flat
                bordered
                @row-click="selectComponent"
              >
                <template #body-cell-actions="props">
                  <q-td :props="props">
                    <q-btn
                      icon="edit"
                      size="sm"
                      flat
                      dense
                      color="primary"
                      @click.stop="editComponent(props.row)"
                    >
                      <q-tooltip>Edit</q-tooltip>
                    </q-btn>

                    <q-btn
                      icon="content_copy"
                      size="sm"
                      flat
                      dense
                      color="secondary"
                      @click.stop="duplicateComponent(props.row)"
                    >
                      <q-tooltip>Duplicate</q-tooltip>
                    </q-btn>

                    <q-btn
                      icon="delete"
                      size="sm"
                      flat
                      dense
                      color="negative"
                      @click.stop="deleteComponent(props.row)"
                    >
                      <q-tooltip>Delete</q-tooltip>
                    </q-btn>
                  </q-td>
                </template>

                <template #body-cell-color="props">
                  <q-td :props="props">
                    <div
                      class="color-preview-small"
                      :style="{ backgroundColor: props.row.color || '#3B82F6' }"
                    ></div>
                  </q-td>
                </template>

                <template #body-cell-slots="props">
                  <q-td :props="props">
                    <q-badge color="green" class="q-mr-xs">
                      {{ props.row.input_slots?.length || 0 }} in
                    </q-badge>
                    <q-badge color="orange">
                      {{ props.row.output_slots?.length || 0 }} out
                    </q-badge>
                  </q-td>
                </template>
              </q-table>
            </div>
          </template>

          <!-- Right Panel: Component Form -->
          <template #after>
            <div class="q-pa-md">
              <div v-if="!selectedComponentType" class="text-center q-pa-xl text-grey-6">
                <q-icon name="library_books" size="5em" />
                <div class="text-h6 q-mt-md">Select a component to edit</div>
                <div class="text-caption">Or create a new one</div>
              </div>

              <div v-else>
                <div class="text-h6 q-mb-md">
                  {{ isEditMode ? 'Edit Component' : 'New Component' }}
                </div>

                <q-form class="q-gutter-md" @submit="saveComponent">
                  <!-- Basic Info -->
                  <q-card flat bordered>
                    <q-card-section>
                      <div class="text-subtitle2 q-mb-sm">Basic Information</div>

                      <q-input
                        v-model="formData.name"
                        label="Name *"
                        dense
                        outlined
                        :rules="[val => !!val || 'Name is required']"
                      />

                      <q-input
                        v-model="formData.category"
                        label="Category"
                        dense
                        outlined
                        class="q-mt-sm"
                      />

                      <q-input
                        v-model="formData.kit_name"
                        label="Kit Name *"
                        dense
                        outlined
                        class="q-mt-sm"
                        :rules="[val => !!val || 'Kit name is required']"
                      />

                      <div class="row q-gutter-sm q-mt-sm">
                        <q-input
                          v-model.number="formData.width"
                          label="Width"
                          type="number"
                          dense
                          outlined
                          class="col"
                        />

                        <q-input
                          v-model.number="formData.height"
                          label="Height"
                          type="number"
                          dense
                          outlined
                          class="col"
                        />
                      </div>

                      <div class="row items-center q-gutter-sm q-mt-sm">
                        <q-input
                          v-model="formData.color"
                          label="Color"
                          dense
                          outlined
                          class="col"
                        >
                          <template #prepend>
                            <div
                              class="color-preview"
                              :style="{ backgroundColor: formData.color || '#3B82F6' }"
                            ></div>
                          </template>
                        </q-input>

                        <q-btn
                          icon="palette"
                          flat
                          @click="showColorPickerDialog = true"
                        />
                      </div>
                    </q-card-section>
                  </q-card>

                  <!-- Input Slots -->
                  <q-card flat bordered>
                    <q-card-section>
                      <div class="row items-center q-mb-sm">
                        <div class="text-subtitle2">Input Slots</div>
                        <q-space />
                        <q-btn
                          icon="add"
                          size="sm"
                          flat
                          dense
                          color="primary"
                          @click="addInputSlot"
                        />
                      </div>

                      <div v-if="formData.input_slots.length === 0" class="text-caption text-grey-6">
                        No input slots defined
                      </div>

                      <div
                        v-for="(slot, index) in formData.input_slots"
                        :key="`in-${index}`"
                        class="row q-gutter-sm q-mb-sm"
                      >
                        <q-input
                          v-model="slot.name"
                          label="Name"
                          dense
                          outlined
                          class="col"
                        />

                        <q-input
                          v-model="slot.type"
                          label="Type"
                          dense
                          outlined
                          class="col"
                        />

                        <q-btn
                          icon="delete"
                          size="sm"
                          flat
                          dense
                          color="negative"
                          @click="removeInputSlot(index)"
                        />
                      </div>
                    </q-card-section>
                  </q-card>

                  <!-- Output Slots -->
                  <q-card flat bordered>
                    <q-card-section>
                      <div class="row items-center q-mb-sm">
                        <div class="text-subtitle2">Output Slots</div>
                        <q-space />
                        <q-btn
                          icon="add"
                          size="sm"
                          flat
                          dense
                          color="primary"
                          @click="addOutputSlot"
                        />
                      </div>

                      <div v-if="formData.output_slots.length === 0" class="text-caption text-grey-6">
                        No output slots defined
                      </div>

                      <div
                        v-for="(slot, index) in formData.output_slots"
                        :key="`out-${index}`"
                        class="row q-gutter-sm q-mb-sm"
                      >
                        <q-input
                          v-model="slot.name"
                          label="Name"
                          dense
                          outlined
                          class="col"
                        />

                        <q-input
                          v-model="slot.type"
                          label="Type"
                          dense
                          outlined
                          class="col"
                        />

                        <q-btn
                          icon="delete"
                          size="sm"
                          flat
                          dense
                          color="negative"
                          @click="removeOutputSlot(index)"
                        />
                      </div>
                    </q-card-section>
                  </q-card>

                  <!-- Default Properties -->
                  <q-card flat bordered>
                    <q-card-section>
                      <div class="row items-center q-mb-sm">
                        <div class="text-subtitle2">Default Properties</div>
                        <q-space />
                        <q-btn
                          icon="add"
                          size="sm"
                          flat
                          dense
                          color="primary"
                          @click="addProperty"
                        />
                      </div>

                      <div v-if="propertyList.length === 0" class="text-caption text-grey-6">
                        No default properties defined
                      </div>

                      <div
                        v-for="(prop, index) in propertyList"
                        :key="`prop-${index}`"
                        class="row q-gutter-sm q-mb-sm"
                      >
                        <q-input
                          v-model="prop.key"
                          label="Key"
                          dense
                          outlined
                          class="col"
                        />

                        <q-input
                          v-model="prop.value"
                          label="Value"
                          dense
                          outlined
                          class="col"
                        />

                        <q-btn
                          icon="delete"
                          size="sm"
                          flat
                          dense
                          color="negative"
                          @click="removeProperty(index)"
                        />
                      </div>
                    </q-card-section>
                  </q-card>

                  <!-- Actions -->
                  <div class="row q-gutter-sm">
                    <q-btn
                      label="Cancel"
                      flat
                      @click="cancelEdit"
                    />

                    <q-space />

                    <q-btn
                      type="submit"
                      label="Save"
                      color="primary"
                      :loading="isSaving"
                    />
                  </div>
                </q-form>
              </div>
            </div>
          </template>
        </q-splitter>
      </q-card-section>
    </q-card>

    <!-- Color Picker Dialog -->
    <q-dialog v-model="showColorPickerDialog">
      <q-card>
        <q-card-section>
          <q-color v-model="formData.color" />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup label="Done" color="primary" flat />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-dialog>
</template>

<script setup>
  import { ref, computed, watch } from 'vue';
  import { useQuasar } from 'quasar';
  import axios from 'axios';

  // Props
  const props = defineProps({
    modelValue: {
      type: Boolean,
      required: true,
    },
  });

  // Emits
  const emit = defineEmits(['update:modelValue', 'component-updated']);

  // Quasar
  const $q = useQuasar();

  // State
  const componentTypes = ref([]);
  const selectedComponentType = ref(null);
  const isEditMode = ref(false);
  const isLoading = ref(false);
  const isSaving = ref(false);
  const searchQuery = ref('');
  const splitterModel = ref(50);
  const showColorPickerDialog = ref(false);

  // Form data
  const formData = ref({
    name: '',
    category: '',
    kit_name: 'sedona',
    width: 120,
    height: 80,
    color: '#3B82F6',
    input_slots: [],
    output_slots: [],
    default_properties: {},
  });

  const propertyList = ref([]); // For editing default_properties

  // Table columns
  const columns = [
    {
      name: 'name',
      label: 'Name',
      field: 'name',
      align: 'left',
      sortable: true,
    },
    {
      name: 'category',
      label: 'Category',
      field: 'category',
      align: 'left',
      sortable: true,
    },
    {
      name: 'kit_name',
      label: 'Kit',
      field: 'kit_name',
      align: 'left',
      sortable: true,
    },
    {
      name: 'color',
      label: 'Color',
      field: 'color',
      align: 'center',
    },
    {
      name: 'slots',
      label: 'Slots',
      align: 'center',
    },
    {
      name: 'usage_count',
      label: 'Usage',
      field: 'usage_count',
      align: 'center',
      sortable: true,
    },
    {
      name: 'actions',
      label: 'Actions',
      align: 'center',
    },
  ];

  const pagination = ref({
    rowsPerPage: 20,
  });

  // Computed

  const filteredComponents = computed(() => {
    if (!searchQuery.value) {
      return componentTypes.value;
    }

    const query = searchQuery.value.toLowerCase();
    return componentTypes.value.filter((comp) =>
      comp.name.toLowerCase().includes(query) ||
      comp.category?.toLowerCase().includes(query) ||
      comp.kit_name?.toLowerCase().includes(query),
    );
  });

  // Methods

  /**
   * Load component types
   */
  async function loadComponentTypes() {
    isLoading.value = true;
    try {
      const response = await axios.get('/v2/sax/component-types');
      componentTypes.value = response.data.data || [];
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to load component types',
        caption: error.message,
      });
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Create new component
   */
  function createNewComponent() {
    selectedComponentType.value = {};
    isEditMode.value = false;

    formData.value = {
      name: '',
      category: '',
      kit_name: 'sedona',
      width: 120,
      height: 80,
      color: '#3B82F6',
      input_slots: [],
      output_slots: [],
      default_properties: {},
    };

    propertyList.value = [];
  }

  /**
   * Select component to view
   */
  function selectComponent(evt, row) {
    editComponent(row);
  }

  /**
   * Edit component
   */
  function editComponent(component) {
    selectedComponentType.value = component;
    isEditMode.value = true;

    formData.value = {
      name: component.name,
      category: component.category || '',
      kit_name: component.kit_name,
      width: component.width,
      height: component.height,
      color: component.color || '#3B82F6',
      input_slots: [...(component.input_slots || [])],
      output_slots: [...(component.output_slots || [])],
      default_properties: { ...(component.default_properties || {}) },
    };

    // Convert default_properties to list
    propertyList.value = Object.entries(formData.value.default_properties).map(([key, value]) => ({
      key,
      value,
    }));
  }

  /**
   * Duplicate component
   */
  function duplicateComponent(component) {
    selectedComponentType.value = {};
    isEditMode.value = false;

    formData.value = {
      name: `${component.name} Copy`,
      category: component.category || '',
      kit_name: component.kit_name,
      width: component.width,
      height: component.height,
      color: component.color || '#3B82F6',
      input_slots: [...(component.input_slots || [])],
      output_slots: [...(component.output_slots || [])],
      default_properties: { ...(component.default_properties || {}) },
    };

    propertyList.value = Object.entries(formData.value.default_properties).map(([key, value]) => ({
      key,
      value,
    }));
  }

  /**
   * Delete component
   */
  function deleteComponent(component) {
    $q.dialog({
      title: 'Delete Component Type',
      message: `Are you sure you want to delete "${component.name}"?`,
      cancel: true,
      persistent: true,
    }).onOk(async () => {
      try {
        await axios.delete(`/v2/sax/component-types/${component.id}`);

        $q.notify({
          type: 'positive',
          message: 'Component type deleted',
        });

        loadComponentTypes();

        if (selectedComponentType.value?.id === component.id) {
          selectedComponentType.value = null;
        }

        emit('component-updated');
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to delete component type',
          caption: error.message,
        });
      }
    });
  }

  /**
   * Save component
   */
  async function saveComponent() {
    isSaving.value = true;

    try {
      // Convert property list to object
      const properties = {};
      propertyList.value.forEach((prop) => {
        if (prop.key) {
          properties[prop.key] = prop.value;
        }
      });

      const data = {
        ...formData.value,
        default_properties: properties,
      };

      let response;
      if (isEditMode.value) {
        response = await axios.put(
          `/v2/sax/component-types/${selectedComponentType.value.id}`,
          data,
        );
      } else {
        response = await axios.post('/v2/sax/component-types', data);
      }

      $q.notify({
        type: 'positive',
        message: `Component type ${isEditMode.value ? 'updated' : 'created'}`,
      });

      loadComponentTypes();
      selectedComponentType.value = null;
      emit('component-updated');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to ${isEditMode.value ? 'update' : 'create'} component type`,
        caption: error.response?.data?.message || error.message,
      });
    } finally {
      isSaving.value = false;
    }
  }

  /**
   * Cancel edit
   */
  function cancelEdit() {
    selectedComponentType.value = null;
  }

  /**
   * Add input slot
   */
  function addInputSlot() {
    formData.value.input_slots.push({
      name: '',
      type: 'any',
    });
  }

  /**
   * Remove input slot
   */
  function removeInputSlot(index) {
    formData.value.input_slots.splice(index, 1);
  }

  /**
   * Add output slot
   */
  function addOutputSlot() {
    formData.value.output_slots.push({
      name: '',
      type: 'any',
    });
  }

  /**
   * Remove output slot
   */
  function removeOutputSlot(index) {
    formData.value.output_slots.splice(index, 1);
  }

  /**
   * Add property
   */
  function addProperty() {
    propertyList.value.push({
      key: '',
      value: '',
    });
  }

  /**
   * Remove property
   */
  function removeProperty(index) {
    propertyList.value.splice(index, 1);
  }

  /**
   * Import library
   */
  async function importLibrary() {
    $q.notify({
      type: 'info',
      message: 'Import functionality coming soon',
    });
  }

  /**
   * Export library
   */
  async function exportLibrary() {
    try {
      const response = await axios.get('/v2/sax/component-types/export', {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `component-library-${Date.now()}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      $q.notify({
        type: 'positive',
        message: 'Component library exported',
      });
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to export component library',
        caption: error.message,
      });
    }
  }

  /**
   * Close dialog
   */
  function closeDialog() {
    emit('update:modelValue', false);
  }

  // Watchers

  watch(
    () => props.modelValue,
    (newValue) => {
      if (newValue) {
        loadComponentTypes();
      }
    },
  );
</script>

<style scoped lang="scss">
.color-preview {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #ccc;
}

.color-preview-small {
  width: 30px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #ccc;
}
</style>
